<template>
  <v-container fluid class="pa-4">
    <v-card class="mb-3">
      <v-card-title class="headline">
        <v-icon left>compare_arrows</v-icon>
        Rakip Fiyatları
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="running" :disabled="running" @click="refreshAll">
          <v-icon left>refresh</v-icon>
          Hepsini Şimdi Çek
        </v-btn>
      </v-card-title>
      <v-card-text v-if="status" class="pt-0">
        <v-layout wrap>
          <v-flex xs12 sm6 md3 class="mb-2">
            <div class="caption grey--text">Son tarama</div>
            <div v-if="running">
              <v-progress-circular indeterminate size="14" width="2" class="mr-1"></v-progress-circular>
              Çekiliyor... ({{ status.run.total }} link, {{ status.run.trigger }})
            </div>
            <div v-else-if="status.run.finishedAt">
              {{ status.run.finishedAt | formatDate }} ({{ status.run.trigger }})
              <span v-if="!status.run.error" class="caption">
                — {{ status.run.success }} başarılı, {{ status.run.failed }} hatalı
              </span>
            </div>
            <div v-else>Uygulama açıldığından beri henüz tarama yapılmadı</div>
          </v-flex>
          <v-flex xs12 sm6 md3 class="mb-2">
            <div class="caption grey--text">Sonraki otomatik tarama</div>
            <div v-if="status.intervalMinutes === 0">Otomatik çekme kapalı</div>
            <div v-else>
              {{ status.nextRunAt ? $options.filters.formatDate(status.nextRunAt) : "Birazdan" }}
              <span class="caption">(her {{ status.intervalMinutes }} dk)</span>
            </div>
          </v-flex>
          <v-flex xs12 sm6 md3 class="mb-2">
            <div class="caption grey--text">Takip edilen link</div>
            <div>{{ status.linkCount }}</div>
          </v-flex>
          <v-flex xs12 sm6 md3 class="mb-2">
            <div class="caption grey--text">Hatalı link</div>
            <div :class="status.errorLinkCount ? 'red--text font-weight-bold' : ''">{{ status.errorLinkCount }}</div>
          </v-flex>
        </v-layout>
        <v-alert :value="!!status.run.error" type="error" outline class="mt-2">
          Son tarama tamamlanamadı: {{ status.run.error }}
        </v-alert>
        <div class="caption grey--text mt-1">
          Rakip linkleri ürün detayındaki "Rakip Linkleri" bölümünden eklenir. Çekme aralığı Uygulama Ayarları'ndaki
          <b>RakipFiyat.AralikDakika</b> ile değiştirilir (0 = kapalı).
        </div>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-text>
        <v-layout wrap align-center>
          <v-flex xs12 sm3 class="pr-2">
            <v-select v-model="tenantId" :items="tenantOptions" label="Tenant" hide-details @change="load"></v-select>
          </v-flex>
          <v-flex xs12 sm3 class="pr-2">
            <v-select v-model="site" :items="siteOptions" label="Site" hide-details @change="load"></v-select>
          </v-flex>
          <v-flex xs12 sm3 class="pr-2">
            <v-text-field v-model="search" label="Ürün / satıcı ara" prepend-icon="search" hide-details clearable></v-text-field>
          </v-flex>
          <v-flex xs12 sm3>
            <v-checkbox v-model="onlyCheaper" label="Yalnızca bizden ucuz rakipler" hide-details></v-checkbox>
          </v-flex>
        </v-layout>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="filtered"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="[50, 100, { text: 'Tümü', value: -1 }]"
        no-data-text="Rakip linki yok"
        class="elevation-0"
      >
        <template v-slot:items="props">
          <td>
            <a href="#" @click.prevent="openProduct(props.item)">{{ props.item.productSku }}</a>
            <div class="caption grey--text">{{ props.item.ourProductName }}</div>
          </td>
          <td>{{ props.item.siteName }}</td>
          <td>
            <a :href="props.item.url" target="_blank" rel="noopener">{{ props.item.sellerName || "Satıcı bilinmiyor" }}</a>
          </td>
          <td class="text-xs-right">
            {{ props.item.price | formatMoney }}
            <div v-if="props.item.price != null && props.item.priceSource === 'Script'">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="api-warn">API'den gelmedi</span>
                </template>
                <span>Sepet fiyatı alınamadı, sayfa fiyatı gösteriliyor.<br />{{ props.item.apiError }}</span>
              </v-tooltip>
            </div>
          </td>
          <td class="text-xs-right">{{ props.item.ourPrice | formatMoney }}</td>
          <td class="text-xs-right" :class="diffClass(props.item)">
            <template v-if="props.item.diff != null">
              {{ props.item.diff | formatMoney }}
              <span class="caption">(%{{ props.item.diffPercent }})</span>
            </template>
            <span v-else>-</span>
          </td>
          <td>{{ props.item.inStock === true ? "Var" : props.item.inStock === false ? "Yok" : "-" }}</td>
          <td>
            <v-tooltip bottom :disabled="!props.item.lastError">
              <template v-slot:activator="{ on }">
                <v-chip small :color="statusColor(props.item.status)" text-color="white" v-on="on">
                  {{ props.item.status }}
                </v-chip>
              </template>
              <span>{{ props.item.lastError }}</span>
            </v-tooltip>
          </td>
          <td class="caption">{{ props.item.lastSuccessAt | formatDate }}</td>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import competitorPrices from "@/api/competitorPrices";
import { COMPETITOR_SITES } from "@/components/CompetitorLinks.vue";

export default {
  data: () => ({
    status: null,
    rows: [],
    loading: false,
    tenantId: 0,
    site: null,
    search: "",
    onlyCheaper: false,
    pollTimer: null,
    tenantOptions: [
      { text: "Tümü", value: 0 },
      { text: "Altında Fırsat", value: 1 },
      { text: "E-Altın", value: 2 }
    ],
    siteOptions: [{ text: "Tümü", value: null }].concat(COMPETITOR_SITES.map(s => ({ text: s.name, value: s.key }))),
    pagination: { sortBy: "diffPercent", descending: false, rowsPerPage: 100 },
    headers: [
      { text: "Ürün", value: "productSku", align: "left" },
      { text: "Site", value: "siteName", align: "left" },
      { text: "Rakip Satıcı", value: "sellerName", align: "left" },
      { text: "Rakip Fiyat", value: "price", align: "right" },
      { text: "Bizim Fiyat", value: "ourPrice", align: "right" },
      { text: "Fark", value: "diffPercent", align: "right" },
      { text: "Stok", value: "inStock", align: "left" },
      { text: "Durum", value: "status", align: "left" },
      { text: "Son Başarılı Çekim", value: "lastSuccessAt", align: "left" }
    ]
  }),
  computed: {
    running() {
      return !!(this.status && this.status.run && this.status.run.isRunning);
    },
    filtered() {
      const q = (this.search || "").toLocaleLowerCase("tr");
      return this.rows.filter(r => {
        if (this.onlyCheaper && !(r.diff < 0)) return false;
        if (!q) return true;
        return [r.productSku, r.ourProductName, r.sellerName, r.productName]
          .some(v => v && v.toLocaleLowerCase("tr").includes(q));
      });
    }
  },
  async created() {
    await Promise.all([this.loadStatus(), this.load()]);
    if (this.running) this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await competitorPrices.overview(this.tenantId || null, this.site);
        this.rows = data || [];
      } finally {
        this.loading = false;
      }
    },
    async loadStatus() {
      const { data } = await competitorPrices.status();
      this.status = data;
    },
    async refreshAll() {
      const { data } = await competitorPrices.refreshAll();
      this.status = data;
      this.startPolling();
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(async () => {
        await this.loadStatus();
        if (!this.running) {
          this.stopPolling();
          await this.load();
        }
      }, 4000);
    },
    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    openProduct(row) {
      // Admin ürün detayı seçili tenant'a göre çalışıyor
      localStorage.setItem("selectedTenantId", String(row.tenantId));
      this.$router.push(`/admin/product/detail/${row.productId}`);
    },
    diffClass(r) {
      if (r.diff == null || r.diff === 0) return "";
      return r.diff < 0 ? "red--text font-weight-bold" : "green--text";
    },
    statusColor(status) {
      return status === "OK" ? "green" : status === "Hata" ? "red" : "grey";
    }
  }
};
</script>

<style scoped>
.api-warn {
  display: inline-block;
  font-size: 11px;
  color: #e65100;
  background: #fff3e0;
  border-radius: 3px;
  padding: 0 4px;
  cursor: help;
  white-space: nowrap;
}
</style>
